import { FocusContext } from '@noriginmedia/norigin-spatial-navigation'
import mux from 'mux-embed'
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { connect } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import Player, {
  PLAYER_ERROR_CODES,
  PLAYER_REPORTABLE_EVENTS,
} from '../../../../shared/components/VideoPlayer/index.js'
import {
  ComcastErrorType,
  getComcastErrorTypeFromPlayerError,
  platformTitle,
  trackError,
} from '../../../../shared/helpers.js'
import {
  KeyTypeEnum,
  useKeyDown,
} from '../../../../shared/providers/KeyDownProvider'
import { useNavigationMode } from '../../../../shared/providers/NavigationModeProvider/NavigationModeProvider'
import { CaptionsButton } from '../../components/CaptionsButton/CaptionsButton'
import ForbiddenActionMessage from '../../components/ForbiddenActionMessage'
import Spinner from '../../components/Spinner/Spinner.js'
import configuration from '../../configuration.js'
import usePlayerNetworkRecovery from '../../hooks/usePlayerNetworkRecovery'
import tts from '../../lib/TTS.js'
import Platform from '../../platforms/index.js'
import { useNetworkMonitor } from '../../providers/NetworkMonitorProvider'
import { store } from '../../store.js'
// eslint-disable-next-line import/no-named-as-default
import useHybridFocusable from '../../hooks/useHybridFocusable/useHybridFocusable'
import type { Channel, RootState } from '../../reducers/types/reducerTypes'
import { useResolveUrsInApp } from '../../videoPlayer/hooks'
import type { LivePlayer, LivePlayerScreenProps, RouterState } from './types'

type StreamData = { url: string; isDrm: boolean; licenseUrl: string | null }
type PlayerError = { code?: string; message?: string; data?: unknown }

const PLAYER_DOM_ID = 'player-live-ga'
declare const COMPILING_FOR_TIZEN: boolean

function getStreamDataFromChannel(channel: Channel): StreamData {
  const streamData: StreamData = { url: '', isDrm: false, licenseUrl: null }
  if (channel.drm_dash_url) {
    streamData.url = channel.drm_dash_url
    streamData.isDrm = true
    streamData.licenseUrl = channel.drm_widevine_license_url
  } else if (channel.non_drm_url) {
    streamData.url = channel.non_drm_url
  }
  return streamData
}

function LivePlayerScreen({
  areCaptionsEnabled,
  device,
  station,
  fallbackStation,
}: LivePlayerScreenProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const ref = useRef<HTMLVideoElement>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [captionsAvailable, setCaptionsAvailable] = useState(false)
  const [spinnerVisible, setSpinnerVisible] = useState(true)
  const [showControls, setShowControls] = useState(false)
  const { setSuppressAutoErrorModal } = useNetworkMonitor()
  const livePlayerRef = useRef<LivePlayer | null>(null)

  const { setHadNetworkError } = usePlayerNetworkRecovery({
    playerRef: livePlayerRef,
  })

  const { isCursorMode } = useNavigationMode()
  const {
    ref: containerRef,
    focusKey,
    focusSelf,
  } = useHybridFocusable({
    isFocusBoundary: true,
    forceFocus: true,
  })
  const { channel } = (location.state as RouterState) || {}
  const stationLogo = station.white_single_logo || station.white_cobranded_logo
  const showLogoOverlay =
    channel.logo_overlay && !fallbackStation.callsign && stationLogo

  const keysToListen = [
    KeyTypeEnum.Right,
    KeyTypeEnum.Left,
    KeyTypeEnum.Rewind,
    KeyTypeEnum.FastForward,
    KeyTypeEnum.Pause,
  ]

  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }
    if (!isCursorMode) {
      timerRef.current = setTimeout(() => {
        setShowControls(false)
        focusSelf()
      }, 3000)
    }
  }, [isCursorMode])
  const streamData = getStreamDataFromChannel(channel)
  const shouldResolveUrl = Platform.shouldResolveUrsUrlInApp()
  const { resolvedUrl, isResolving } = useResolveUrsInApp(streamData.url, {
    enabled: shouldResolveUrl && !!streamData.url,
  })

  useKeyDown([KeyTypeEnum.Down, KeyTypeEnum.Up, KeyTypeEnum.Enter], () => {
    setShowControls(true)
    resetTimer()
  })

  useKeyDown([KeyTypeEnum.Back, KeyTypeEnum.Stop], () => {
    if (showControls) {
      setShowControls(false)
      focusSelf()
    } else {
      tts.pushTextToRead('Exiting livestream')
      navigate(
        `/livestream?fromChannelPlayback=true&channelFeedCID=${channel.feed_cid}`,
        { replace: true }
      )
    }
  })

  // Workaround to allow new focus components to work with spatial navigation and
  // old components work with focusable HoC
  // TODO: remove this when whole app is migrated to react spatial navigation
  useEffect(() => {
    window.focusedComponent = {}
    window.focusedComponent.handleBySpatialNavigation = true
    return () => {
      window.focusedComponent.handleBySpatialNavigation = false
    }
  }, [])

  const reportError = useCallback(
    (eventName: string, eventData: PlayerError = {}) => {
      const { code, message, data } = eventData

      switch (eventName) {
        case PLAYER_REPORTABLE_EVENTS.ERROR: {
          const errorCode = code || PLAYER_ERROR_CODES.UNKNOWN_ERROR
          mux.emit(`#${PLAYER_DOM_ID}`, 'error', {
            player_error_code: errorCode,
            player_error_message: message || 'Live player Error',
            player_error_context: data || eventData,
          })

          // Check if this is a network error
          const errorType = getComcastErrorTypeFromPlayerError(
            errorCode,
            !channel.drm_dash_url, // isHlsPlayer
            data
          )
          const isNetworkError = errorType === ComcastErrorType.network

          if (isNetworkError) {
            setHadNetworkError(true)
          } else {
            // For non-network errors, navigate to error page as before
            navigate(`/live-playback/error?error-code=${errorCode}`)
          }
          break
        }
        case PLAYER_REPORTABLE_EVENTS.BUFFER_DEPLETED:
          setSuppressAutoErrorModal(false)
          break

        case PLAYER_REPORTABLE_EVENTS.BUFFER_RECOVERED:
          setSuppressAutoErrorModal(true)
          setHadNetworkError(false)
          break

        default:
          break
      }
    },
    [channel.drm_dash_url, navigate, setSuppressAutoErrorModal]
  )

  const initPlayer = useCallback(() => {
    const { feed_cid: pbsLivestreamChannelContentId } = channel
    const { full_name: liveStreamChannelName } = channel

    Platform.startPlayback({
      sendMediaEvents: true,
      extraParams: {
        ...((pbsLivestreamChannelContentId || undefined) && {
          pbs_livestream_channel_content_id: pbsLivestreamChannelContentId,
        }),
        ...((liveStreamChannelName || undefined) && {
          livestream_player_name: liveStreamChannelName,
        }),
        ...Platform.getLiveStreamAnalyticsParams(),
      },
    })

    if (livePlayerRef.current) livePlayerRef.current.init()
    const stationCallsign = fallbackStation
      ? fallbackStation.callsign || ''
      : station.call_sign || ''

    mux.monitor(`#${PLAYER_DOM_ID}`, {
      disableCookies: true,
      beaconCollectionDomain: 'qoe.pbs.org',
      respectDoNotTrack: true,
      data: {
        env_key: configuration?.advertising?.muxPropertyKey,
        video_stream_type: 'live',
        video_title: `Livestream ${
          channel.short_name || ''
        } ${stationCallsign}`,
        video_id: '-1',
        player_init_time: Date.now(),
        player_name: 'tizen-ga-live',
        viewer_device_name: platformTitle(),
        viewer_device_model: device.deviceModel,
        experiment_name: 'ga-live',
        player_version: livePlayerRef.current?.getVersion() || 'unknown',
      },
    })
  }, [])

  useEffect(() => {
    if (isResolving) return

    tts.addTextToRead('Starting livestream')

    const contentlUrl = shouldResolveUrl ? resolvedUrl : streamData.url

    livePlayerRef.current = streamData.isDrm
      ? Platform.createLiveDRMPlayer(ref, {
          drmWidevideLicenseUrl: streamData.licenseUrl,
          contentSourceUrl: contentlUrl,
          setCaptionsAvailable,
          reportPlayerEvent: reportError,
        })
      : Platform.createLivePlayer(ref, {
          contentSourceUrl: contentlUrl,
          setCaptionsAvailable,
          reportPlayerEvent: reportError,
        })
    initPlayer()
  }, [isResolving, resolvedUrl])

  useLayoutEffect(() => {
    if (!spinnerVisible) {
      focusSelf()
    }
  }, [focusSelf, spinnerVisible])

  useEffect(
    () => () => {
      if (livePlayerRef.current) livePlayerRef.current.destroy()
      Platform.endPlayback()
    },
    []
  )

  useEffect(() => {
    if (showControls) {
      resetTimer()
    }
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [showControls])

  const toggleCaptions = useCallback(() => {
    store.dispatch({
      type: 'SET_CAPTION_STATE',
      payload: { enabled: !areCaptionsEnabled },
    })
    tts.addTextToRead(`Captions ${areCaptionsEnabled ? 'Off' : 'On'}`)
  }, [areCaptionsEnabled])

  useEffect(() => {
    if (isCursorMode && !showControls) {
      setShowControls(true)
      return
    }

    if (!isCursorMode && showControls) {
      resetTimer()
    }
  }, [isCursorMode])

  return (
    <FocusContext.Provider value={focusKey}>
      <ForbiddenActionMessage keysToListen={keysToListen} />
      <div ref={containerRef} className="live-player-screen">
        <Spinner visible={spinnerVisible || isResolving} />
        <Player
          ref={ref}
          onError={(e: Error) => {
            reportError(PLAYER_REPORTABLE_EVENTS.ERROR, e)
            trackError(e)
            navigate(
              `/live-playback/error?error-code=${PLAYER_ERROR_CODES.MEDIA}`
            )
          }}
          captionsAvailable={captionsAvailable}
          onTimeUpdate={() => setSpinnerVisible(false)}
          domID={PLAYER_DOM_ID}
        />
        {captionsAvailable && !COMPILING_FOR_TIZEN && (
          <CaptionsButton
            isVisible={showControls}
            active={areCaptionsEnabled}
            onEnter={toggleCaptions}
            stealFocus
          />
        )}
        {showLogoOverlay && !spinnerVisible && !isResolving && (
          <img
            className="station-bug"
            src={stationLogo}
            alt={`${station.call_sign} logo`}
          />
        )}
      </div>
    </FocusContext.Provider>
  )
}

const mapStateToProps = (state: RootState) => ({
  areCaptionsEnabled: state.captions.enabled,
  device: state.device,
  station: state.station,
  fallbackStation: state.station.fallback_station,
})

export default connect(mapStateToProps)(LivePlayerScreen)
